import {FC, FormEvent, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, useLocation, useSearchParams} from 'react-router-dom'
import {BulkOfferSeed} from '../../products/add-bulk/core/extractBarcodes'
import BarcodeOfferCard from './BarcodeOfferCard'
import '../../common/hisbeh-integrity.css'

/**
 * B1 — multi-barcode offer page. The bulk equivalent of "Sell Yours",
 * styled after the duplicate-barcode resolution page: one card per
 * barcode, each resolving its own create/update inline (see
 * BarcodeOfferCard).
 *
 * Barcodes arrive either:
 *   - via navigation state `{ barcodes: string[] }` (from Bulk Add's
 *     used-barcode rows — wired in B2), or
 *   - via `?barcodes=a,b,c` (bookmark / direct link), or
 *   - typed/scanned one at a time here (standalone use).
 */

type Outcome = 'created' | 'updated'

// Barcodes can legitimately contain hyphens/dots (e.g. "100463-NVY",
// "J02DMA.0006K.C4067"). Only trim/collapse whitespace — never strip internal
// characters, or the value won't match the catalog (backend trims only too).
const normalize = (raw: string): string => raw.trim().replace(/\s+/g, '')

const parseList = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const v of values) {
    const b = normalize(v)
    if (b !== '' && !seen.has(b)) {
      seen.add(b)
      out.push(b)
    }
  }
  return out
}

// Each card fires its own GET /offers/lookup-barcode on mount. Paginate so a
// large list (e.g. a 96-row bulk add) doesn't fan out 96 concurrent lookups —
// only the current page's cards mount + look up at a time.
const PAGE_SIZE = 10

const FromBarcodesPage: FC = () => {
  const {formatMessage} = useIntl()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const initial = useMemo(() => {
    const fromState = (location.state as {barcodes?: string[]} | null)?.barcodes ?? []
    const fromQuery = (searchParams.get('barcodes') ?? '').split(',')
    return parseList([...fromState, ...fromQuery])
    // location.state / query are read once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set when the supplier arrives here straight after a Bulk Add (B2): their
  // genuinely-new barcodes were already filed as product requests, so the
  // banner explains why some rows below say "not in our catalog".
  const fromBulkAdd = Boolean((location.state as {fromBulkAdd?: boolean} | null)?.fromBulkAdd)

  // Offer values lifted from the uploaded Bulk Add sheet (price/qty/sku per
  // barcode), used to pre-fill each card. Read once on mount.
  const seeds = useMemo(
    () => (location.state as {seeds?: Record<string, BulkOfferSeed>} | null)?.seeds ?? {},
    // location.state is read once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const [barcodes, setBarcodes] = useState<string[]>(initial)
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({})
  const [draft, setDraft] = useState('')
  const [addError, setAddError] = useState<string | null>(null)

  const addBarcode = (e: FormEvent) => {
    e.preventDefault()
    const b = normalize(draft)
    if (b === '') return
    if (barcodes.includes(b)) {
      setAddError(formatMessage({id: 'offers.from_barcodes.duplicate'}))
      return
    }
    setBarcodes((prev) => [...prev, b])
    setDraft('')
    setAddError(null)
  }

  const removeBarcode = (barcode: string) => {
    setBarcodes((prev) => prev.filter((b) => b !== barcode))
    setOutcomes((prev) => {
      const next = {...prev}
      delete next[barcode]
      return next
    })
  }

  const onResolved = (barcode: string, outcome: Outcome) => {
    setOutcomes((prev) => ({...prev, [barcode]: outcome}))
  }

  const [currentPage, setCurrentPage] = useState(1)

  const created = Object.values(outcomes).filter((o) => o === 'created').length
  const updated = Object.values(outcomes).filter((o) => o === 'updated').length
  const pending = barcodes.length - created - updated

  // Clamp the page (barcodes can shrink via remove) and slice for render.
  const totalPages = Math.max(1, Math.ceil(barcodes.length / PAGE_SIZE))
  const page = Math.min(currentPage, totalPages)
  const pageBarcodes = barcodes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // Prominent pager, rendered both above and below the cards so it's visible
  // without scrolling past a full page of cards.
  const pager =
    totalPages > 1 ? (
      <div className="card shadow-sm mb-5">
        <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3 py-3">
          <button
            type="button"
            className="btn btn-primary"
            disabled={page <= 1}
            onClick={() => setCurrentPage(page - 1)}
          >
            <i className="fas fa-chevron-left me-1" aria-hidden="true" />
            {formatMessage({id: 'offers.from_barcodes.prev'})}
          </button>
          <span className="fw-bold fs-5 text-gray-800">
            {formatMessage({id: 'offers.from_barcodes.page_of'}, {page, total: totalPages})}
          </span>
          <button
            type="button"
            className="btn btn-primary"
            disabled={page >= totalPages}
            onClick={() => setCurrentPage(page + 1)}
          >
            {formatMessage({id: 'offers.from_barcodes.next'})}
            <i className="fas fa-chevron-right ms-1" aria-hidden="true" />
          </button>
        </div>
      </div>
    ) : null

  return (
    <div className="hib-root">
      <Link to="/request-add-products" className="hib-breadcrumb">
        <i className="fas fa-arrow-left" aria-hidden="true" />
        {formatMessage({id: 'add_single_product'})}
      </Link>
      <h1 className="hib-title">{formatMessage({id: 'offers.from_barcodes.page_title'})}</h1>

      {barcodes.length > 0 ? (
        <div className="hib-summary">
          <div className="hib-summary-item hib-summary-item--created">
            <i className="fas fa-circle-check" aria-hidden="true" />
            <span>
              <span className="hib-num">{created}</span> {formatMessage({id: 'bpr_created'})}
            </span>
          </div>
          <div className="hib-summary-sep" />
          <div className="hib-summary-item hib-summary-item--updated">
            <i className="fas fa-rotate" aria-hidden="true" />
            <span>
              <span className="hib-num">{updated}</span> {formatMessage({id: 'bpr_updated'})}
            </span>
          </div>
          <div className="hib-summary-sep" />
          <div className="hib-summary-item hib-summary-item--pending">
            <span className="hib-summary-dot" />
            <span>
              {pending} {formatMessage({id: 'bpr_pending'})}
            </span>
          </div>
          <Link to="/offers/list" className="hib-summary-link">
            {formatMessage({id: 'offers.from_barcodes.view_offers'})}
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </Link>
        </div>
      ) : null}

      {fromBulkAdd ? (
        <div className="alert alert-info d-flex align-items-center" role="status">
          <i className="fas fa-info-circle me-2" aria-hidden="true" />
          <span>{formatMessage({id: 'offers.from_barcodes.from_bulk_add'})}</span>
        </div>
      ) : null}

      <div className="card mb-5">
        <div className="card-body">
          <p className="text-muted mb-4">{formatMessage({id: 'offers.from_barcodes.page_subtitle'})}</p>

          <form onSubmit={addBarcode} className="d-flex gap-2 align-items-start">
            <div className="flex-grow-1">
              <input
                type="text"
                className="form-control"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={formatMessage({id: 'offers.from_barcodes.add_barcode_placeholder'})}
                aria-label={formatMessage({id: 'offers.from_barcodes.add_barcode'})}
              />
              {addError ? <div className="text-danger fs-8 mt-1">{addError}</div> : null}
            </div>
            <button type="submit" className="btn btn-primary" disabled={normalize(draft) === ''}>
              {formatMessage({id: 'offers.from_barcodes.add_btn'})}
            </button>
          </form>
        </div>
      </div>

      {barcodes.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-10">
            {formatMessage({id: 'offers.from_barcodes.empty'})}
          </div>
        </div>
      ) : (
        <>
          {pager}
          {pageBarcodes.map((b) => (
            <BarcodeOfferCard
              key={b}
              barcode={b}
              onResolved={onResolved}
              onRemove={removeBarcode}
              fromBulkAdd={fromBulkAdd}
              seed={seeds[b]}
            />
          ))}
          {pager}
        </>
      )}
    </div>
  )
}

export default FromBarcodesPage
